import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Settings, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, Edit, Check, X, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';

const STORAGE_KEY = 'client-datagrid-v1';
const PRESETS_KEY = 'client-datagrid-presets-v1';

// Every column that can be shown. `path` is used by inline edit to PATCH the
// client doc via `/api/admin/clients/{id}/field`. Columns without `path` are
// read-only (e.g. derived / computed / reference fields).
const ALL_COLUMNS = [
  // ── Identity
  { key: 'name',                label: 'Name',                get: c => c.name, path: 'name' },
  { key: 'company_name',        label: 'Company Name',        get: c => c.company_name, path: 'company_name' },
  { key: 'category',            label: 'Category',            get: c => c.category, path: 'category' },
  { key: 'industry',            label: 'Industry',            get: c => c.industry, path: 'industry' },
  { key: 'color',               label: 'Colour',              get: c => c.color, path: 'color' },
  { key: 'is_active',           label: 'Active?',             get: c => c.is_active === false ? 'No' : 'Yes', path: 'is_active' },
  { key: 'source',              label: 'Source',              get: c => c.source, path: 'source' },
  // ── Address
  { key: 'company_address',     label: 'Address',             get: c => c.company_address, path: 'company_address' },
  { key: 'company_state',       label: 'State',               get: c => c.company_state, path: 'company_state' },
  { key: 'company_pin_code',    label: 'PIN',                 get: c => c.company_pin_code || c.pin_code, path: 'company_pin_code' },
  // ── Contact
  { key: 'contact_person',      label: 'Contact Person',      get: c => c.contact_person, path: 'contact_person' },
  { key: 'contact_email',       label: 'Contact Email',       get: c => c.contact_email, path: 'contact_email', inputType: 'email' },
  { key: 'whatsapp_number',     label: 'WhatsApp Number',     get: c => c.whatsapp_number, path: 'whatsapp_number' },
  { key: 'contact_phone',       label: 'Contact Phone',       get: c => c.contact_phone, path: 'contact_phone' },
  { key: 'phone',               label: 'Legacy Phone',        get: c => c.phone, path: 'phone' },
  // ── Legal
  { key: 'gstin',               label: 'GSTIN',               get: c => c.gstin, path: 'gstin' },
  { key: 'pan',                 label: 'PAN',                 get: c => c.pan, path: 'pan' },
  { key: 'gst_treatment',       label: 'GST Treatment',       get: c => c.gst_treatment, path: 'gst_treatment' },
  // ── Billing
  { key: 'currency',            label: 'Currency',            get: c => c.currency, path: 'currency' },
  { key: 'billing_cycle',       label: 'Billing Cycle',       get: c => c.billing_cycle, path: 'billing_cycle' },
  { key: 'payment_type',        label: 'Payment Type',        get: c => c.payment_type, path: 'payment_type' },
  { key: 'payment_terms',       label: 'Payment Terms (days)', get: c => c.payment_terms, path: 'payment_terms', type: 'number', inputType: 'number' },
  { key: 'credit_limit',        label: 'Credit Limit',        get: c => c.credit_limit, path: 'credit_limit', type: 'currency', inputType: 'number' },
  { key: 'brand_anniversary_date', label: 'Brand Anniversary', get: c => c.brand_anniversary_date, path: 'brand_anniversary_date', type: 'date', inputType: 'date' },
  // ── Retainer
  { key: 'retainer',            label: 'Retainer',            get: c => c.retainer || c.retainer_value, path: 'retainer', type: 'currency', inputType: 'number' },
  { key: 'retainer_billing_cycle', label: 'Retainer Cycle',   get: c => c.retainer_billing_cycle, path: 'retainer_billing_cycle' },
  { key: 'retainer_start_date', label: 'Retainer Start',      get: c => c.retainer_start_date, path: 'retainer_start_date', type: 'date', inputType: 'date' },
  { key: 'retainer_end_date',   label: 'Retainer End',        get: c => c.retainer_end_date, path: 'retainer_end_date', type: 'date', inputType: 'date' },
  { key: 'retainer_is_active',  label: 'Retainer Active?',    get: c => c.retainer_is_active === true ? 'Yes' : c.retainer_is_active === false ? 'No' : '', path: 'retainer_is_active' },
  { key: 'retainer_status',     label: 'Retainer Status',     get: c => c.retainer_status, path: 'retainer_status' },
  { key: 'retainer_auto_archive', label: 'Auto-archive?',     get: c => c.retainer_auto_archive === true ? 'Yes' : c.retainer_auto_archive === false ? 'No' : '', path: 'retainer_auto_archive' },
  // ── Assignment (read-only computed)
  { key: 'departments',         label: 'Departments',         get: c => Array.isArray(c.departments) ? c.departments.join(', ') : c.departments },
  { key: 'pocs',                label: 'POCs (dept → emp)',   get: c => c.pocs && typeof c.pocs === 'object' ? Object.entries(c.pocs).filter(([, v]) => v).map(([k]) => k).join(', ') : '' },
  // ── Meta (read-only)
  { key: 'imported_from',       label: 'Imported From',       get: c => c.imported_from },
  { key: 'is_archived',         label: 'Archived?',           get: c => c.is_archived ? 'Yes' : 'No' },
  { key: 'created_at',          label: 'Created',             get: c => c.created_at, type: 'date' },
  { key: 'updated_at',          label: 'Updated',             get: c => c.updated_at, type: 'date' },
];

const DEFAULT_VISIBLE = new Set([
  'name', 'company_name', 'category', 'company_state', 'contact_email',
  'whatsapp_number', 'contact_phone', 'gstin', 'pan', 'currency',
  'billing_cycle', 'payment_type', 'retainer', 'retainer_is_active', 'is_active',
]);

// Built-in presets — one-tap layouts for common workflows.
const BUILT_IN_PRESETS = [
  { name: 'Overview',       columns: ['name', 'company_name', 'category', 'is_active', 'retainer', 'billing_cycle', 'currency'] },
  { name: 'Billing view',   columns: ['name', 'gstin', 'pan', 'gst_treatment', 'currency', 'billing_cycle', 'payment_type', 'payment_terms', 'credit_limit'] },
  { name: 'Retainer view',  columns: ['name', 'retainer', 'retainer_billing_cycle', 'retainer_start_date', 'retainer_end_date', 'retainer_is_active', 'retainer_status', 'retainer_auto_archive'] },
  { name: 'Contact view',   columns: ['name', 'company_name', 'contact_person', 'contact_email', 'whatsapp_number', 'contact_phone', 'company_address', 'company_state', 'company_pin_code'] },
  { name: 'WhatsApp view',  columns: ['name', 'contact_person', 'whatsapp_number', 'contact_phone', 'contact_email'] },
  { name: 'Legal & Tax',    columns: ['name', 'company_name', 'gstin', 'pan', 'gst_treatment', 'company_state', 'company_address'] },
];

function loadPrefs() {
  try { const r = localStorage.getItem(STORAGE_KEY); if (r) return JSON.parse(r); } catch { /* noop */ }
  return { order: ALL_COLUMNS.map(c => c.key), visible: [...DEFAULT_VISIBLE] };
}
function savePrefs(p) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* noop */ } }
function loadUserPresets() {
  try { const r = localStorage.getItem(PRESETS_KEY); if (r) return JSON.parse(r); } catch { /* noop */ }
  return [];
}
function saveUserPresets(list) { try { localStorage.setItem(PRESETS_KEY, JSON.stringify(list)); } catch { /* noop */ } }

function formatCell(value, type) {
  if (value === undefined || value === null || value === '') return null;
  if (type === 'date') { try { const d = new Date(value); return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return String(value); } }
  if (type === 'currency') {
    const n = Number(value); return isNaN(n) ? String(value) : `₹${n.toLocaleString('en-IN')}`;
  }
  if (type === 'number') return String(value);
  if (typeof value === 'object') { try { return JSON.stringify(value); } catch { return '—'; } }
  return String(value);
}

// Inline editable cell — click to edit, Enter to save, Esc to cancel.
function EditableCell({ client, col, initialValue, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (initialValue == null ? '' : String(initialValue)));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const missing = initialValue === undefined || initialValue === null || initialValue === '';
  const displayed = missing ? '—' : formatCell(initialValue, col.type);

  const commit = async () => {
    if (saving) return;
    if (String(draft) === String(initialValue ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await adminApi.patch(`/api/admin/clients/${client._id}/field`, { path: col.path, value: draft });
      onSaved(res.data.value);
      toast.success('Saved');
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const cancel = () => { setDraft(initialValue == null ? '' : String(initialValue)); setEditing(false); };

  if (!col.path) {
    return <span className={missing ? 'text-red-400' : 'text-neutral-800'} title={missing ? 'Read-only column' : String(initialValue)}>{displayed}</span>;
  }
  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-[180px]" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type={col.inputType || 'text'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          disabled={saving}
          className="flex-1 px-1.5 py-0.5 border border-blue-400 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
          data-testid={`client-grid-input-${client._id}-${col.key}`}
        />
        <button onClick={commit} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`client-grid-save-${client._id}-${col.key}`}><Check className="w-3.5 h-3.5" /></button>
        <button onClick={cancel} disabled={saving} className="p-1 text-neutral-500 hover:bg-neutral-100 rounded"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className={`text-left w-full block ${missing ? 'text-red-400 italic' : 'text-neutral-800'} hover:underline decoration-dotted underline-offset-2`}
      title={missing ? 'Click to fill' : 'Click to edit'}
      data-testid={`client-grid-cell-btn-${client._id}-${col.key}`}
    >
      {displayed}
    </button>
  );
}

export default function ClientDataGrid({ clients: incoming, onEdit }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [showSettings, setShowSettings] = useState(false);
  const [userPresets, setUserPresets] = useState(loadUserPresets);
  const [clients, setClients] = useState(incoming || []);
  const [search, setSearch] = useState('');
  useEffect(() => { setClients(incoming || []); }, [incoming]);

  const columns = useMemo(() => {
    const byKey = Object.fromEntries(ALL_COLUMNS.map(c => [c.key, c]));
    return prefs.order.map(k => byKey[k]).filter(Boolean);
  }, [prefs.order]);
  const visibleColumns = useMemo(() => columns.filter(c => prefs.visible.includes(c.key)), [columns, prefs.visible]);

  const filteredClients = useMemo(() => {
    if (!search) return clients;
    const q = search.toLowerCase();
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.company_name || '').toLowerCase().includes(q) ||
      (c.category || '').toLowerCase().includes(q) ||
      (c.gstin || '').toLowerCase().includes(q) ||
      (c.pan || '').toLowerCase().includes(q) ||
      (c.contact_email || '').toLowerCase().includes(q) ||
      (c.whatsapp_number || '').toLowerCase().includes(q) ||
      (c.contact_phone || '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  const update = (next) => { setPrefs(next); savePrefs(next); };
  const toggleColumn = (key) => update({ ...prefs, visible: prefs.visible.includes(key) ? prefs.visible.filter(k => k !== key) : [...prefs.visible, key] });
  const moveColumn = (key, dir) => {
    const idx = prefs.order.indexOf(key); if (idx < 0) return;
    const next = [...prefs.order]; const target = idx + dir; if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update({ ...prefs, order: next });
  };
  const resetLayout = () => update({ order: ALL_COLUMNS.map(c => c.key), visible: [...DEFAULT_VISIBLE] });
  const applyPreset = (columnKeys) => update({ ...prefs, visible: columnKeys.filter(k => ALL_COLUMNS.some(c => c.key === k)) });
  const saveNamedPreset = () => {
    const name = window.prompt('Save current column layout as preset — name it:', '');
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    const next = [...userPresets.filter(p => p.name !== cleanName), { name: cleanName, columns: [...prefs.visible] }];
    setUserPresets(next); saveUserPresets(next);
    toast.success(`Preset "${cleanName}" saved`);
  };
  const deletePreset = (name) => {
    if (!window.confirm(`Delete preset "${name}"?`)) return;
    const next = userPresets.filter(p => p.name !== name);
    setUserPresets(next); saveUserPresets(next);
  };

  // Apply an inline edit to the local clients state so the cell re-renders
  // without a full refetch.
  const setClientField = (id, path, value) => {
    setClients(prev => prev.map(c => c._id === id ? { ...c, [path]: value } : c));
  };

  const allPresets = [...BUILT_IN_PRESETS.map(p => ({ ...p, builtIn: true })), ...userPresets];

  return (
    <div className="soft-card overflow-hidden" data-testid="client-datagrid">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/40 bg-white/40 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-neutral-500">
            <span className="font-semibold text-neutral-700">{filteredClients.length}</span>
            {' '}{search ? `of ${clients.length}` : ''} client{filteredClients.length === 1 ? '' : 's'} ·
            {' '}<span className="font-semibold text-neutral-700">{visibleColumns.length}</span> / {ALL_COLUMNS.length} columns ·
            {' '}<span className="text-red-500">click any cell to edit</span>
          </p>
          {allPresets.map(p => (
            <button
              key={p.name}
              onClick={() => applyPreset(p.columns)}
              className="group inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white/80 border border-white/60 rounded-full hover:bg-blue-50 hover:border-blue-300"
              data-testid={`client-preset-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
              title={p.builtIn ? `Built-in preset · ${p.columns.length} cols` : `Custom preset · ${p.columns.length} cols`}
            >
              {p.name}
              {!p.builtIn && (
                <span onClick={(e) => { e.stopPropagation(); deletePreset(p.name); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700">
                  <Trash2 className="w-3 h-3" />
                </span>
              )}
            </button>
          ))}
          <button onClick={saveNamedPreset} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full hover:bg-emerald-100" data-testid="client-preset-save">
            <Plus className="w-3 h-3" /> Save as preset
          </button>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, GSTIN, PAN…"
            className="px-3 py-1.5 border border-white/50 rounded-lg text-sm w-56 focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="client-grid-search"
          />
          <div className="relative">
            <button onClick={() => setShowSettings(v => !v)} className="px-3 py-1.5 border border-white/50 rounded-lg text-sm text-neutral-700 hover:bg-white/70 flex items-center gap-1.5" data-testid="client-grid-settings-toggle">
              <Settings className="w-3.5 h-3.5" /> Customize columns
            </button>
            {showSettings && (
              <div className="absolute right-0 top-full mt-2 w-72 max-h-[70vh] overflow-y-auto bg-white border border-neutral-200 rounded-xl shadow-2xl z-50 p-2" data-testid="client-grid-settings">
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-neutral-100 mb-1">
                  <span className="text-xs font-semibold text-neutral-700">Columns · drag order with arrows</span>
                  <button onClick={resetLayout} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="client-grid-reset-layout"><RotateCcw className="w-3 h-3" /> Reset</button>
                </div>
                {columns.map((c, i) => {
                  const on = prefs.visible.includes(c.key);
                  return (
                    <div key={c.key} className="flex items-center gap-1 px-1.5 py-1 hover:bg-neutral-50 rounded">
                      <button onClick={() => toggleColumn(c.key)} className="p-1 hover:bg-neutral-100 rounded" data-testid={`client-grid-toggle-${c.key}`}>
                        {on ? <Eye className="w-3.5 h-3.5 text-emerald-600" /> : <EyeOff className="w-3.5 h-3.5 text-neutral-400" />}
                      </button>
                      <span className={`flex-1 text-sm ${on ? 'text-neutral-800' : 'text-neutral-400 line-through'}`}>{c.label}</span>
                      <button onClick={() => moveColumn(c.key, -1)} disabled={i === 0} className="p-1 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                      <button onClick={() => moveColumn(c.key, +1)} disabled={i === columns.length - 1} className="p-1 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-auto max-h-[75vh] relative">
        <table className="min-w-max text-sm border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-white/95 backdrop-blur border-b border-neutral-200">
              {visibleColumns.map((c, i) => (
                <th key={c.key} className={`px-3 py-2 text-left text-xs font-semibold text-neutral-600 whitespace-nowrap border-r border-neutral-100 ${i === 0 ? 'sticky left-0 bg-white z-10 shadow-[2px_0_0_rgba(0,0,0,0.03)]' : ''}`}>{c.label}</th>
              ))}
              <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600 whitespace-nowrap sticky right-0 bg-white z-10 shadow-[-2px_0_0_rgba(0,0,0,0.03)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 1} className="px-6 py-12 text-center text-neutral-500">No clients found.</td></tr>
            ) : filteredClients.map(client => (
              <tr key={client._id} className="hover:bg-blue-50/40 border-b border-neutral-100" data-testid={`client-grid-row-${client._id}`}>
                {visibleColumns.map((c, i) => {
                  const raw = c.get(client);
                  const missing = raw === undefined || raw === null || raw === '';
                  return (
                    <td key={c.key} className={`px-3 py-2 whitespace-nowrap border-r border-neutral-100 ${missing ? 'bg-red-50' : ''} ${i === 0 ? 'sticky left-0 z-[5] font-medium ' + (missing ? 'bg-red-50' : 'bg-white') : ''}`} data-testid={`client-grid-cell-${client._id}-${c.key}`}>
                      <EditableCell
                        client={client}
                        col={c}
                        initialValue={raw}
                        onSaved={(newVal) => { if (c.path) setClientField(client._id, c.path, newVal); }}
                      />
                    </td>
                  );
                })}
                <td className="px-3 py-2 whitespace-nowrap sticky right-0 bg-white z-[5] shadow-[-2px_0_0_rgba(0,0,0,0.03)]">
                  <button onClick={() => onEdit && onEdit(client)} className="px-2 py-1 border border-neutral-200 rounded-lg text-xs text-neutral-700 hover:bg-neutral-50 flex items-center gap-1" data-testid={`client-grid-edit-${client._id}`}>
                    <Edit className="w-3 h-3" /> Full edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
